// Service Worker Registration for Pulsate Global
// Handles SW registration and background sync

const DB_NAME = 'pulsate_offline_db';
const DB_VERSION = 1;
const QUEUE_STORE = 'message_queue';
const TOKEN_STORE = 'auth_token';
const SYNC_TAG = 'pulsate-message-sync';

class ServiceWorkerManager {
  constructor() {
    this.registration = null;
    this.isSupported = 'serviceWorker' in navigator;
    this.isSyncSupported = 'serviceWorker' in navigator && 'SyncManager' in window;
    this.listeners = new Set();
  }

  // Initialize and register service worker
  async init() {
    if (!this.isSupported) {
      console.log('[SWManager] Service workers not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      
      console.log('[SWManager] Service worker registered:', this.registration.scope);

      // Handle updates
      this.registration.addEventListener('updatefound', () => {
        console.log('[SWManager] New service worker found');
        const newWorker = this.registration.installing;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available
            this.notifyListeners({ type: 'UPDATE_AVAILABLE' });
          }
        });
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[SWManager] Message from SW:', event.data);
        this.handleSWMessage(event.data);
      });

      // Wait for active registration
      await navigator.serviceWorker.ready;
      console.log('[SWManager] Service worker ready');

      return true;
    } catch (error) {
      console.error('[SWManager] Registration failed:', error);
      return false;
    }
  }

  // Subscribe to SW events
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify listeners
  notifyListeners(event) {
    this.listeners.forEach(cb => cb(event));
  }

  // Handle messages from service worker
  handleSWMessage(data) {
    switch (data.type) {
      case 'MESSAGE_SYNCED':
        // Dispatch event for UI update
        window.dispatchEvent(new CustomEvent('messageSynced', {
          detail: { tempId: data.tempId, serverMessage: data.serverMessage }
        }));
        this.notifyListeners(data);
        break;
      
      case 'SYNC_COMPLETE':
        console.log('[SWManager] Background sync complete');
        this.notifyListeners(data);
        // Reload queue in UI
        window.dispatchEvent(new CustomEvent('syncComplete'));
        break;
      
      default:
        this.notifyListeners(data);
    }
  }

  // Request background sync
  async requestSync() {
    if (!this.isSyncSupported || !this.registration) {
      console.log('[SWManager] Background sync not supported, using fallback');
      return false;
    }

    try {
      await this.registration.sync.register(SYNC_TAG);
      console.log('[SWManager] Background sync registered');
      return true;
    } catch (error) {
      console.error('[SWManager] Failed to register sync:', error);
      return false;
    }
  }

  // Store message in IndexedDB for service worker access
  async queueMessageForSync(message) {
    try {
      const db = await this.openDB();
      const transaction = db.transaction(QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(QUEUE_STORE);
      
      await new Promise((resolve, reject) => {
        const request = store.put(message);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log('[SWManager] Message queued in IndexedDB:', message.id);

      // Request background sync
      if (this.isSyncSupported) {
        await this.requestSync();
      }

      return true;
    } catch (error) {
      console.error('[SWManager] Failed to queue message:', error);
      return false;
    }
  }

  // Store auth token for service worker access
  async storeToken(token) {
    try {
      const db = await this.openDB();
      const transaction = db.transaction(TOKEN_STORE, 'readwrite');
      const store = transaction.objectStore(TOKEN_STORE);
      
      await new Promise((resolve, reject) => {
        const request = store.put({ key: 'auth_token', value: token });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log('[SWManager] Token stored in IndexedDB');
      return true;
    } catch (error) {
      console.error('[SWManager] Failed to store token:', error);
      return false;
    }
  }

  // Get all queued messages from IndexedDB
  async getQueuedMessages() {
    try {
      const db = await this.openDB();
      const transaction = db.transaction(QUEUE_STORE, 'readonly');
      const store = transaction.objectStore(QUEUE_STORE);
      
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[SWManager] Failed to get queue:', error);
      return [];
    }
  }

  // Remove message from IndexedDB queue
  async removeFromQueue(messageId) {
    try {
      const db = await this.openDB();
      const transaction = db.transaction(QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(QUEUE_STORE);
      
      await new Promise((resolve, reject) => {
        const request = store.delete(messageId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log('[SWManager] Message removed from IndexedDB:', messageId);
      return true;
    } catch (error) {
      console.error('[SWManager] Failed to remove message:', error);
      return false;
    }
  }

  // Clear all queued messages
  async clearQueue() {
    try {
      const db = await this.openDB();
      const transaction = db.transaction(QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(QUEUE_STORE);
      
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log('[SWManager] Queue cleared');
      return true;
    } catch (error) {
      console.error('[SWManager] Failed to clear queue:', error);
      return false;
    }
  }

  // Open IndexedDB
  openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
        }
        
        if (!db.objectStoreNames.contains(TOKEN_STORE)) {
          db.createObjectStore(TOKEN_STORE, { keyPath: 'key' });
        }
      };
    });
  }

  // Check for updates
  async checkForUpdates() {
    if (this.registration) {
      await this.registration.update();
    }
  }

  // Skip waiting and activate new service worker
  async activateUpdate() {
    if (this.registration && this.registration.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  // Get SW registration state
  getState() {
    return {
      isSupported: this.isSupported,
      isSyncSupported: this.isSyncSupported,
      isRegistered: !!this.registration,
      scope: this.registration?.scope || null
    };
  }
}

// Create singleton instance
const serviceWorkerManager = new ServiceWorkerManager();

export default serviceWorkerManager;
